import { SuggestionWrapper } from "$components/autocomplete/suggestion";
import {
  Amp,
  RXDB_AMP_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/amp";
import {
  type ClinicalProductCategory,
  RXDB_CLINICAL_PRODUCT_CATEGORY_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/clinical-product-category";
import {
  type ClinicalProductForm,
  RXDB_CLINICAL_PRODUCT_FORM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/clinical-product-form";
import {
  RXDB_VMP_COLLECTION_NAME,
  type Vmp,
} from "$global-providers/rxdb-provider/collections/vmp";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { useUser } from "$global-providers/user-provider";
import SupplyLevelChip from "$routes/_lib/inventory/supply-level-chip";
import { CircularProgress, TextField, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import type { SupplyLevel } from "elephant-web/gql";
import { ClinicalProductType, ProductType } from "elephant-web/gql";
import Fuse from "fuse.js";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useController } from "react-hook-form";
import { useIntl } from "react-intl";
import { useRxData } from "rxdb-hooks";
import { useDebouncedCallback } from "use-debounce";
import { usePrescribeClinicalProductSearchWithInventoryLazyQuery } from "./__generated__/use-clinical-product-search.gql";

export enum ClinicalProductAutocompleteMode {
  OUTPATIENT = "outpatient",
  INPATIENT = "inpatient",
}

const productTypeToClinicalProductType = (
  productType: ProductType,
): ClinicalProductType => {
  switch (productType) {
    case ProductType.Vmp:
      return ClinicalProductType.Vmp;
    case ProductType.Amp:
      return ClinicalProductType.Amp;
    default:
      return ClinicalProductType.Vmp;
  }
};

type FuseItem = {
  id: string;
  title: string;
  keywords: string[];
  form: string;
  formId: string;
  category: string;
  inventory?: {
    supplyLevel: SupplyLevel;
    quantity: number;
  };
  productType: ClinicalProductType;
};

const buildDrugName = ({ title, form }: FuseItem) =>
  form ? `${title} (${form})` : title;

const Option: FC<Fuse.FuseResult<FuseItem>> = ({ item, matches, ...rest }) => {
  const string = buildDrugName(item);
  return (
    <SuggestionWrapper {...rest}>
      <Typography variant="body1">{string}</Typography>
      &nbsp;
      {item.inventory ? (
        <SupplyLevelChip
          supplyLevel={item.inventory.supplyLevel}
          quantity={item.inventory.quantity}
        />
      ) : (
        <Typography
          variant="caption"
          style={{
            color: "#666",
            fontSize: "0.75rem",
            fontStyle: "italic",
            marginLeft: "8px",
          }}
        >
          Stock info unavailable
        </Typography>
      )}
    </SuggestionWrapper>
  );
};

type ClinicalProductAutocompleteProps = {
  style?: React.CSSProperties;
  clinicalProductChangeSideEffects?: (input: { formId: string }) => void;
  locationId?: string;
  allowFreeTextPrescriptions?: boolean;
} & (
  | {
      mode: ClinicalProductAutocompleteMode.OUTPATIENT;
      clinicalProductIdName: string;
      clinicalProductTypeName: string;
      freetextName: string;
    }
  | {
      mode: ClinicalProductAutocompleteMode.INPATIENT;
      name: string;
    }
);

export const ClinicalProductAutocomplete: FC<
  ClinicalProductAutocompleteProps
> = (props) => {
  const {
    style,
    clinicalProductChangeSideEffects,
    locationId,
    allowFreeTextPrescriptions = true,
  } = props;
  const { formatMessage } = useIntl();

  const { facilityId } = useUser();
  const isOutpatientMode =
    props.mode === ClinicalProductAutocompleteMode.OUTPATIENT;

  const primaryController = useController({
    name: isOutpatientMode
      ? (
          props as Extract<
            ClinicalProductAutocompleteProps,
            { mode: ClinicalProductAutocompleteMode.OUTPATIENT }
          >
        ).clinicalProductIdName
      : (
          props as Extract<
            ClinicalProductAutocompleteProps,
            { mode: ClinicalProductAutocompleteMode.INPATIENT }
          >
        ).name,
  });

  const typeController = useController({
    name: isOutpatientMode
      ? (
          props as Extract<
            ClinicalProductAutocompleteProps,
            { mode: ClinicalProductAutocompleteMode.OUTPATIENT }
          >
        ).clinicalProductTypeName
      : `${(props as Extract<ClinicalProductAutocompleteProps, { mode: ClinicalProductAutocompleteMode.INPATIENT }>).name}_dummy_type`,
    shouldUnregister: !isOutpatientMode,
  });

  const freetextController = useController({
    name: isOutpatientMode
      ? (
          props as Extract<
            ClinicalProductAutocompleteProps,
            { mode: ClinicalProductAutocompleteMode.OUTPATIENT }
          >
        ).freetextName
      : `${(props as Extract<ClinicalProductAutocompleteProps, { mode: ClinicalProductAutocompleteMode.INPATIENT }>).name}_dummy_freetext`,
    shouldUnregister: !isOutpatientMode,
  });

  const [inpatientDisplayText, setInpatientDisplayText] = useState("");

  useEffect(() => {
    if (!isOutpatientMode && primaryController.field.value?.title) {
      setInpatientDisplayText(primaryController.field.value.title);
    }
  }, [isOutpatientMode, primaryController.field.value?.title]);

  const [searchInventoryLevels, { data: inventoryData }] =
    usePrescribeClinicalProductSearchWithInventoryLazyQuery({
      errorPolicy: "ignore",
      onError: () => {},
    });

  const [inventoryCache, setInventoryCache] = useState<
    Record<string, { supplyLevel: SupplyLevel; quantity: number }>
  >({});

  const { result: vmpResult } = useRxData<Vmp>(
    RXDB_VMP_COLLECTION_NAME,
    (collection) => collection.find({ selector: {} }),
  );

  const { result: ampResult } = useRxData<Amp>(
    RXDB_AMP_COLLECTION_NAME,
    (collection) => collection.find({ selector: {} }),
  );

  const { result: formResult } = useRxData<ClinicalProductForm>(
    RXDB_CLINICAL_PRODUCT_FORM_COLLECTION_NAME,
    (collection) => collection.find({ selector: {} }),
  );

  const { result: categoryResult } = useRxData<ClinicalProductCategory>(
    RXDB_CLINICAL_PRODUCT_CATEGORY_COLLECTION_NAME,
    (collection) => collection.find({ selector: {} }),
  );

  const {
    replicationState: {
      vmp: { isPulling: isVmpPulling },
      amp: { isPulling: isAmpPulling },
      clinicalProductCategory: { isPulling: isCategoryPulling },
      clinicalProductForm: { isPulling: isFormPulling },
    },
  } = useRxDBContext();

  const [formMap, categoryMap, vmpMap] = useMemo(() => {
    const formMap =
      formResult?.reduce(
        (acc, form) => {
          acc[form.id] = form.title.en;
          return acc;
        },
        {} as Record<string, string>,
      ) ?? null;

    const categoryMap =
      categoryResult?.reduce(
        (acc, category) => {
          acc[category.id] = category.title.en;
          return acc;
        },
        {} as Record<string, string>,
      ) ?? null;

    const vmpMap =
      vmpResult?.reduce(
        (acc, vmp) => {
          acc[vmp.id] = vmp;
          return acc;
        },
        {} as Record<string, Vmp>,
      ) ?? null;

    return [formMap, categoryMap, vmpMap];
  }, [formResult, categoryResult, vmpResult]);

  const [searchResults, setSearchResults] = useState<
    Fuse.FuseResult<FuseItem>[]
  >([]);

  useEffect(() => {
    if (inventoryData?.productSearch) {
      const newCacheEntries = inventoryData.productSearch.reduce(
        (acc, product) => {
          if (product.inventory) {
            acc[product.id] = {
              supplyLevel: product.inventory.supplyLevel,
              quantity: product.inventory.quantity,
            };
          }
          return acc;
        },
        {} as Record<string, { supplyLevel: SupplyLevel; quantity: number }>,
      );

      setInventoryCache((prev) => ({ ...prev, ...newCacheEntries }));
    }
  }, [inventoryData]);

  const findInventoryForProduct = useCallback(
    (productId: string) => {
      return inventoryCache[productId];
    },
    [inventoryCache],
  );

  const [fuse, fuseLoading] = useMemo(() => {
    if (
      !vmpResult ||
      vmpResult.length === 0 ||
      !formMap ||
      !categoryMap ||
      !vmpMap ||
      !ampResult ||
      ampResult.length === 0
    ) {
      return [null, true];
    }

    const allProducts = [
      ...vmpResult.map<FuseItem>((v) => ({
        id: v.id,
        title: v.title,
        keywords: v.keywords,
        form: formMap[v.formId] ?? "Unknown form",
        formId: v.formId,
        category: categoryMap[v.categoryId] ?? "Unknown category",
        productType: productTypeToClinicalProductType(v.productType),
      })),
      ...ampResult.map<FuseItem>((a) => {
        const parentVmp = vmpMap?.[a.vmpId];
        return {
          id: a.id,
          title: a.title,
          keywords: a.keywords,
          form: parentVmp && formMap ? (formMap[parentVmp.formId] ?? "") : "",
          formId: parentVmp?.formId ?? "",
          category:
            parentVmp && categoryMap
              ? (categoryMap[parentVmp.categoryId] ?? "")
              : "",
          productType: productTypeToClinicalProductType(a.productType),
        };
      }),
    ];

    return [
      new Fuse<FuseItem>(allProducts, {
        shouldSort: true,
        includeMatches: true,
        threshold: 0.5,
        location: 0,
        distance: 100,
        minMatchCharLength: 1,
        keys: [
          {
            name: "keywords",
            weight: 0.3,
          },
          { name: "title", weight: 0.7 },
          { name: "form", weight: 0.2 },
          { name: "category", weight: 0.5 },
        ],
      }),
      isVmpPulling || isFormPulling || isCategoryPulling || isAmpPulling,
    ];
  }, [
    vmpResult,
    formMap,
    categoryMap,
    vmpMap,
    ampResult,
    isVmpPulling,
    isFormPulling,
    isCategoryPulling,
    isAmpPulling,
  ]);

  const debouncedSetSearchResults = useDebouncedCallback(
    (searchTerm: string) => {
      if (!fuse) {
        setSearchResults([]);
        return;
      }

      const fuseResults = fuse.search(searchTerm);

      if (searchTerm.trim() && facilityId) {
        searchInventoryLevels({
          variables: {
            searchTerm: searchTerm.trim(),
            inventoryInput: { facilityId, locationId },
          },
        });
      }

      setSearchResults(fuseResults);
    },
    300,
  );

  return (
    <Autocomplete
      loading={fuseLoading}
      options={searchResults}
      getOptionLabel={(option) => buildDrugName(option.item)}
      noOptionsText="Drug not found."
      renderInput={(params) => (
        <TextField
          {...params}
          error={
            (isOutpatientMode && !!freetextController.fieldState.error) ||
            !!primaryController.fieldState.error
          }
          helperText={(() => {
            const errorMessage =
              (isOutpatientMode &&
                freetextController.fieldState.error?.message) ||
              primaryController.fieldState.error?.message;

            if (!errorMessage) return "";

            // Handle intl message objects
            if (
              typeof errorMessage === "object" &&
              errorMessage &&
              "id" in errorMessage &&
              "defaultMessage" in errorMessage
            ) {
              const intlMessage = errorMessage as {
                id: string;
                defaultMessage: string;
                values?: Record<string, string | number | boolean>;
              };
              return formatMessage(intlMessage, intlMessage.values || {});
            }

            return String(errorMessage);
          })()}
          label="Drug"
          type="text"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {fuseLoading ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                    data-testid="loading-spinner"
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(option) => (
        <Option
          key={option.item.id}
          {...option}
          item={{
            ...option.item,
            inventory: findInventoryForProduct(option.item.id),
          }}
        />
      )}
      filterOptions={(options) => options.slice(0, 25)}
      getOptionSelected={(option, value) => option.item.id === value.item.id}
      onInputChange={(_e, val, reason) => {
        if (reason === "input") {
          debouncedSetSearchResults(val);

          if (isOutpatientMode) {
            primaryController.field.onChange("");
            typeController.field.onChange(null);
            if (allowFreeTextPrescriptions) {
              freetextController.field.onChange(val);
            }
          } else {
            primaryController.field.onChange(null);
            setInpatientDisplayText(val);
          }
        } else if (reason === "clear") {
          if (isOutpatientMode) {
            primaryController.field.onChange("");
            typeController.field.onChange(null);
            freetextController.field.onChange("");
          } else {
            primaryController.field.onChange(null);
            setInpatientDisplayText("");
          }
        }
        return val;
      }}
      inputValue={
        isOutpatientMode ? freetextController.field.value : inpatientDisplayText
      }
      value={
        isOutpatientMode
          ? (searchResults.find(
              (r) => r.item.id === primaryController.field.value,
            ) ?? null)
          : (searchResults.find(
              (r) => r.item.id === primaryController.field.value?.id,
            ) ?? null)
      }
      onChange={(e, val: Fuse.FuseResult<FuseItem> | null, reason) => {
        if (reason === "select-option" && val) {
          if (isOutpatientMode) {
            primaryController.field.onChange(val.item.id);
            typeController.field.onChange(val.item.productType);
            freetextController.field.onChange(buildDrugName(val.item));
          } else {
            const clinicalProduct = {
              id: val.item.id,
              title: buildDrugName(val.item),
              productType: val.item.productType.toLowerCase(),
            };
            primaryController.field.onChange(clinicalProduct);
            setInpatientDisplayText(buildDrugName(val.item));
          }
        } else if (reason === "clear") {
          if (isOutpatientMode) {
            primaryController.field.onChange("");
            typeController.field.onChange(null);
            freetextController.field.onChange("");
          } else {
            primaryController.field.onChange(null);
            setInpatientDisplayText("");
          }
        }

        if (val?.item?.formId && clinicalProductChangeSideEffects) {
          clinicalProductChangeSideEffects({ formId: val.item.formId });
        }
        return val;
      }}
      onBlur={primaryController.field.onBlur}
      style={style}
    />
  );
};
